import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import ApiAnalyticsService, { type HeatmapCell } from '../../../Services/ApiAnalyticsService';
import Notification from '../../Layout/Notification';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const ALL_ENDPOINTS = '__all__';

interface ApiUsageHeatmapProps {
  clientId: string;
  fromIso: string | null;
  toIso: string | null;
}

/**
 * Request-intensity heatmap (ISO day-of-week x UTC hour) fed by the analytics heatmap endpoint.
 * Cells across endpoint families are summed unless one endpoint is selected.
 */
const ApiUsageHeatmap = ({ clientId, fromIso, toIso }: ApiUsageHeatmapProps) => {
  const [cells, setCells] = useState<HeatmapCell[]>([]);
  const [loading, setLoading] = useState(false);
  const [endpoint, setEndpoint] = useState<string>(ALL_ENDPOINTS);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    // endpoint filtering happens client-side on the fetched cells, so changing the
    // endpoint dropdown does not refetch
    if (!clientId) {
      setCells([]);
      return;
    }
    const id = ++fetchIdRef.current;
    setLoading(true);
    ApiAnalyticsService.getClientHeatmap(clientId, { from: fromIso, to: toIso })
      .then(response => {
        if (id !== fetchIdRef.current) return;
        setCells(Array.isArray(response.data) ? response.data : []);
      })
      .catch((err: unknown) => {
        if (id === fetchIdRef.current) {
          Notification.fromError(err, { fallbackTitle: "Couldn't load usage heatmap" });
        }
      })
      .finally(() => {
        if (id === fetchIdRef.current) setLoading(false);
      });
  }, [clientId, fromIso, toIso]);

  useEffect(() => {
    setEndpoint(ALL_ENDPOINTS);
  }, [clientId]);

  const endpoints = useMemo(
    () => Array.from(new Set(cells.map(cell => cell.endpoint))).sort(),
    [cells]
  );

  const { grid, max } = useMemo(() => {
    const counts = new Map<string, number>();
    let maxCount = 0;
    for (const cell of cells) {
      if (endpoint !== ALL_ENDPOINTS && cell.endpoint !== endpoint) continue;
      const key = `${cell.dayOfWeek}|${cell.hour}`;
      const next = (counts.get(key) ?? 0) + cell.requestCount;
      counts.set(key, next);
      if (next > maxCount) maxCount = next;
    }
    return { grid: counts, max: maxCount };
  }, [cells, endpoint]);

  if (!clientId) return null;

  return (
    <Box className="aa-heatmap">
      <Box className="aa-heatmap-toolbar">
        <Typography variant="subtitle2" className="aa-heatmap-title">
          Usage intensity (day / hour, UTC)
        </Typography>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="aa-heatmap-endpoint-label">Endpoint</InputLabel>
          <Select
            labelId="aa-heatmap-endpoint-label"
            label="Endpoint"
            value={endpoint}
            onChange={e => setEndpoint(e.target.value)}
          >
            <MenuItem value={ALL_ENDPOINTS}>All endpoints</MenuItem>
            {endpoints.map(value => (
              <MenuItem key={value} value={value}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {grid.size === 0 && !loading ? (
        <Box className="aa-empty-state">No usage recorded in the selected range.</Box>
      ) : (
        <Box className="aa-heatmap-grid" sx={{ opacity: loading ? 0.5 : 1 }}>
          <div className="aa-heatmap-corner" />
          {HOURS.map(hour => (
            <div key={`h-${hour}`} className="aa-heatmap-hour">
              {hour}
            </div>
          ))}
          {DAY_LABELS.map((label, dayIndex) => (
            <React.Fragment key={label}>
              <div className="aa-heatmap-day">{label}</div>
              {HOURS.map(hour => {
                const count = grid.get(`${dayIndex + 1}|${hour}`) ?? 0;
                const intensity = max > 0 ? count / max : 0;
                return (
                  <div
                    key={`${label}-${hour}`}
                    className="aa-heatmap-cell"
                    title={`${label} ${String(hour).padStart(2, '0')}:00 UTC - ${count.toLocaleString()} requests`}
                    style={{
                      backgroundColor:
                        count > 0
                          ? `rgba(94, 51, 191, ${0.12 + intensity * 0.78})`
                          : 'var(--t-surface-subtle)',
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ApiUsageHeatmap;
